import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import skillLevel from "../data/skillLevel";
import preference from "../data/preference";
import Rating from "../components/Rating";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { saveRating, saveFeedback, saveSatisfaction } from "../tinyDB";

const RecommendationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { recommendations } = location.state || { recommendations: [] };
  const { itemDetails } = location.state || { itemDetails: [] };
  const { user_id } = location.state;

  const MAX_DESC_LENGTH = 100;

  const capitalizeFirstLetter = (str) => {
    return str
      .split(' ')
      .map(word => 
        word.split('-')
          .map(subWord => subWord.charAt(0).toUpperCase() + subWord.slice(1).toLowerCase())
          .join('-')
      )
      .join(' ');
  };

  const getSportLabel = (id) => {
    const item = itemDetails.find(detail => detail.item_id === id);
    return item ? capitalizeFirstLetter(item.Name) : "Unknown Sport";
  };

  const [feedback, setFeedback] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(true);
  // const [showMore, setShowMore] = useState(false);
  // const [listConfirmed, setListConfirmed] = useState(false);
  const [userSatisfaction, setUserSatisfaction] = useState(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackMessageType, setFeedbackMessageType] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [expandedItems, setExpandedItems] = useState({});

  const handleGoBack = () => {
    setFeedback("");
    // setShowMore(false);
    // setListConfirmed(false);
    setUserSatisfaction(null);
    navigate("/");
  };

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleDetails = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const normalizeText = (text) => {
    if (!text) return "";
    return text
      .split(/[,;]/)
      .map((item) => item.trim())
      .join("; ");
  };

  const CATEGORY_MAP = Object.fromEntries(preference.map(({ value, label }) => [value, label]));
  const LEVEL_MAP = Object.fromEntries(skillLevel.map(({ value, label }) => [value, label]));

  const handleRate = async (itemId, itemName, ratingValue) => {
    try {
      await saveRating(user_id, itemId, itemName, ratingValue);
    } catch (error) {
      console.error("Error updating rating:", error);
    }
  };

  const handleFeedbackSubmit = async () => {
    setIsSubmittingFeedback(true);
    try {
      await saveFeedback(user_id, feedback);
  
      setFeedbackSuccess(true);
      setFeedbackMessage("Feedback submitted successfully!");
      setFeedbackMessageType("success");

      setTimeout(() => {
        setFeedbackSuccess(false);
        setFeedbackMessage("");
      }, 2000);
    } catch (error) {
      console.error("Error submitting feedback:", error);

      setFeedbackMessage("Failed to submit feedback, please try again later.");
      setFeedbackMessageType("error");
      setTimeout(() => setFeedbackMessage(""), 2000);

    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // const handleShowMore = () => {
  //   setShowMore(true);
  //   setUserSatisfaction(null);
  // };

  // const handleThumbsUp = async () => {
  //   setListConfirmed(true);
  //   setUserSatisfaction("satisfied");

  //   try {
  //     await saveSatisfaction(user_id, "satisfied");
  
  //   } catch (error) {
  //     console.error("Error saving satisfaction:", error);
  //   }
  // };

  // const handleThumbsDown = async () => {
  //   setUserSatisfaction("unsatisfied");
  //   if (showMore) {
  //     setListConfirmed(true);
  //   }

  //   try {
  //     await saveSatisfaction(user_id, "unsatisfied");
  //   } catch (error) {
  //     console.error("Error updating dissatisfaction:", error);
  //   }
  // };

  const handleUserSatisfaction = async (label) => {
    setUserSatisfaction(label);
    // setListConfirmed(true);
  
    try {
      await saveSatisfaction(user_id, label);
    } catch (error) {
      console.error("Error saving satisfaction:", error);
    }
  };  

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      setShowScrollTop(scrollTop > 50);
      setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });

  return (
    <div className="container mt-3 pb-3">
      <h2 className="fw-bold text-center">Recommended Sports & Exercises</h2>
      <p className="text-muted fst-italic mt-3">
        <span className="fw-bold"><strong>*</strong> Please rate the recommendations based on how relevant they are to you. Your feedback helps us improve the model and diversify future recommendations.<br /></span>
        👎 <strong>1 star</strong> = Not interested in this item at all (e.g., irrelevant or not suitable)<br />
        ❤️ <strong>5 stars</strong> = Very relevant and interesting (e.g., matches your preferences or interests)
      </p>
      {recommendations.length > 0 ? (
        <ul className="list-group">
          {recommendations.map(id => {
            const normalizedId = id.toString().startsWith("100") ? parseInt(id) : parseInt(id) + 1;
            const item = itemDetails.find((detail) => parseInt(detail.item_id) === normalizedId);
            if (!item) return null;
    
            const isExpanded = expandedItems[id];
            const isExpandedDesc = expandedDescriptions[id];

            const categoryText = CATEGORY_MAP[item.Category] || item.Category

            const levelText = item.Level
              ? item.Level.split(",").map(lvl => LEVEL_MAP[parseInt(lvl.trim())] || lvl).join("; ").replace(/;/g, "; ")
              : "Unknown";

            const typeText = normalizeText(item.Type);
            const bodyPartText = normalizeText(item.BodyPart);
            const equipmentText = normalizeText(item.Equipment);

            return (
              <li key={normalizedId} className="list-group-item">
                <div className=" d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mt-2">{getSportLabel(normalizedId)}</h4>
                  <Rating itemId={id} itemName={getSportLabel(normalizedId)} onRate={handleRate} />
                </div>
                {isExpanded && (
                  <div className="mt-3 fst-italic ps-2 border-start border-3 border-secondary">
                    <div>
                      <span className="fw-bold">Description: </span>
                      <span dangerouslySetInnerHTML={{ __html: isExpandedDesc ? item.Desc : item.Desc.slice(0, MAX_DESC_LENGTH) + (item.Desc.length > MAX_DESC_LENGTH ? "..." : "") }} />
                      {item.Desc.length > MAX_DESC_LENGTH && (
                        <button 
                          className="btn btn-link p-0 ms-2"
                          onClick={() => toggleDescription(id)}
                        >
                          {isExpandedDesc ? "Show Less" : "Show More"}
                        </button>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <p><span className="fw-bold">Category:</span> {categoryText}</p>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <p><span className="fw-bold">Training Types:</span> {typeText}</p>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <p><span className="fw-bold">Body Parts involved:</span> {bodyPartText}</p>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <p><span className="fw-bold">Equipments needed:</span> {equipmentText || "None"}</p>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <p><span className="fw-bold">Suitable for:</span> {levelText}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="text-center my-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${id}`}>{isExpanded ? "Click to hide details" : "Click to view more details"}</Tooltip>}
                  >
                    <span
                      className="cursor-pointer"
                      style={{ color: "#3498db", fontSize: "1.2rem", cursor: "pointer" }}
                      onClick={() => toggleDetails(id)}
                    >
                      <i className={`bi ${isExpanded ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}></i>
                    </span>
                  </OverlayTrigger>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2 border-top pt-2">
                  <span className="text-muted fst-italic">{item.explanation}</span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>No recommendations found.</p>
      )}

      {/* {!listConfirmed ? (
        <div className="mt-3 d-flex align-items-center">
          <span className="me-2 fw-bold">Is the list relevant?</span>
          <button 
            className={`btn btn-outline-success me-2 ${userSatisfaction === "satisfied" ? "active" : ""}`} 
            onClick={handleThumbsUp}
          >
            👍
          </button>
          <button 
            className={`btn btn-outline-danger ${userSatisfaction === "unsatisfied" ? "active" : ""}`} 
            onClick={handleThumbsDown}
          >
            👎
          </button>
        </div>
      ) : (
        <p className="text-success mt-3"><strong>{userSatisfaction === "satisfied" ? "Great! Thanks for your feedback." : "Thanks for your feedback!"}</strong></p>
      )} */}

      <div className="mt-3 d-flex flex-column align-items-center">
        <span className="fw-bold text-center">How relevant are these recommendations?</span>

        <div className="btn-group mt-2 d-flex flex-wrap justify-content-center" style={{ width: "100%" }}>
          {["Very Dissatisfied", "Unsatisfied", "Neutral", "Satisfied", "Very Satisfied"].map((label) => (
            <button
              key={label}
              className={`btn ${userSatisfaction === label ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleUserSatisfaction(label)}
              style={{
                flex: "1 1 auto",
                minWidth: "60px",
                maxWidth: "20%",
                padding: "5px",
                fontSize: "1rem",
                whiteSpace: "normal",
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* {!listConfirmed && userSatisfaction === "unsatisfied" && !showMore && (
        <button className="btn btn-link text-primary mt-2 fst-italic" onClick={handleShowMore}>
          Not satisfied? Click to show more
        </button>
      )} */}

      <div className="mt-4">
        <label className="fw-bold">Leave Your Feedback (Optional):</label>
        <div className="feedback-box p-3 rounded shadow-sm d-flex flex-column">
          <textarea
            className="form-control"
            rows="3"
            placeholder="Write any feedback, suggestions, or comments..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          ></textarea>
          <div className="d-flex justify-content-end mt-2">
            <button className="btn btn-primary d-flex align-items-center justify-content-center"
              onClick={handleFeedbackSubmit}
              disabled={isSubmittingFeedback || feedback.trim() === ""}
              style={{ minWidth: "120px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {isSubmittingFeedback ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : feedbackSuccess ? (
                <i className="bi bi-check-lg"
                  style={{
                    fontSize: "1.8rem",
                    color: "#ffffff",
                    fontWeight: "bold",
                    animation: "pop-in 0.3s ease-out",
                  }}
                ></i>
              ) : (
                "Submit Feedback"
              )}
            </button>
          </div>
        </div>
      </div>
      <button className="btn btn-primary mt-3" onClick={handleGoBack}>
        Go Back
      </button>

      <div className="position-fixed end-0 bottom-0 p-3 d-flex flex-column gap-2" style={{ right: "20px", bottom: "20px" }}>
        {showScrollTop && (
          <button className="btn btn-light rounded-circle p-2 shadow-sm scroll-btn" onClick={scrollToTop} style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-arrow-up" style={{ fontSize: "1.5rem", color: "#555" }}></i>
          </button>
        )}
        {showScrollBottom && (
          <button className="btn btn-light rounded-circle p-2 shadow-sm scroll-btn" onClick={scrollToBottom} style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-arrow-down" style={{ fontSize: "1.5rem", color: "#555" }}></i>
          </button>
        )}
      </div>

      {feedbackMessage && (
        <div 
          style={{
            position: "fixed",
            bottom: "20px",
            left: "20px",
            backgroundColor: feedbackMessageType === "success" ? "#d4edda" : "#f8d7da",
            color: feedbackMessageType === "success" ? "#155724" : "#721c24",
            padding: "10px 20px",
            borderRadius: "5px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "opacity 0.5s ease-in-out",
            opacity: feedbackMessage ? 1 : 0,
            zIndex: 1000,
          }}
        >
          {feedbackMessage}
        </div>
      )}
    </div>
  );
};

export default RecommendationsPage;
